// @flow

import { observer } from "mobx-react";
import React from "react";

import TextExtractor from "app/util/TextExtractor";

import AnimatedIconCheck from "app/components/AnimatedIconCheck/AnimatedIconCheck";
import AnimatedIconLoading from "app/components/AnimatedIconLoading/AnimatedIconLoading";

import "./VideoSearch.scss";

export const statuses = {
    NEUTRAL: "neutral",
    SUCCESS: "success",
    ERROR: "error",
    LOADING: "loading"
};

const messages = {
    success: "YouTube video found!",
    error: "Video isn't valid or embeddable.",
    loading: "One moment, looking for your video..."
};

const redirectTimeMilliseconds = 2000;
const errorTimeoutMilliseconds = 2000;

type Props = {
    onUrl: Function,
    autoRedirect: boolean,
    placeholder?: string
};

type State = {
    text: string,
    status: string,
    placeholder: string
};

@observer
export default class VideoSearch extends React.Component<Props, State> {
    timeoutId: TimeoutID;

    constructor(props: Props) {
        super(props);

        this.state = {
            status: statuses.NEUTRAL,
            text: "",
            placeholder: ""
        };
    }

    handleRedirect(href: string): void {
        window.location.href = href;
    }

    handleInput(event: SyntheticInputEvent<HTMLInputElement>): void {
        clearTimeout(this.timeoutId);
        this.setState({ text: event.target.value, status: statuses.NEUTRAL });
    }

    handlePaste(event: SyntheticClipboardEvent<HTMLInputElement>): void {
        clearTimeout(this.timeoutId);
        event.preventDefault();

        const text = TextExtractor.stripVideo(
            event.clipboardData.getData("text/plain")
        );
        let status = statuses.NEUTRAL;

        status = statuses.LOADING;

        this.props.onUrl(text).then(
            ({ data }) => {
                if (text !== this.state.text) return;
                this.setState({ status: statuses.SUCCESS });

                if (this.props.autoRedirect) {
                    this.timeoutId = setTimeout(
                        () => this.handleRedirect(data),
                        redirectTimeMilliseconds
                    );
                }
            },
            () => {
                if (text !== this.state.text) return;

                this.setState({ status: statuses.ERROR });
                setTimeout(
                    () => this.setState({ status: statuses.NEUTRAL }),
                    errorTimeoutMilliseconds
                );
            }
        );

        this.setState({ text, status });
    }

    render() {
        const classes = ["VideoSearch"];
        if (this.state.status)
            classes.push("VideoSearch--" + this.state.status);

        return (
            <div className={classes.join(" ")}>
                {this.renderInput()}
                {this.renderStatus()}
            </div>
        );
    }

    /**
     * Workaround for Safari.
     *
     * When pasting the URL, the handlePaste() calls event.preventDefault()
     * which causes Safari not to clean the placeholder making it difficult
     * to read since the URL is hovering the placeholder.
     */
    get placeholder(): string {
        if (this.state.text !== "") {
            return "";
        }

        return (
            this.props.placeholder ||
            "Create yours. Paste a YouTube url here..."
        );
    }

    renderInput() {
        if (this.state.status !== statuses.NEUTRAL) return null;

        return (
            <input
                type="text"
                name="q"
                className="form-control VideoSearch__input"
                defaultValue={this.state.text}
                onInput={this.handleInput.bind(this)}
                onPaste={this.handlePaste.bind(this)}
                placeholder={this.placeholder}
            />
        );
    }

    renderStatus() {
        if (this.state.status === statuses.NEUTRAL) return null;

        return (
            <div className="VideoSearch__status">
                {this.renderStatusIcon()}
                {this.renderStatusMessage()}
            </div>
        );
    }

    renderStatusIcon() {
        let status;
        switch (this.state.status) {
            case statuses.SUCCESS:
                status = <AnimatedIconCheck />;
                break;
            case statuses.ERROR:
                status = <i className="fa fa-frown-o" />;
                break;
            case statuses.LOADING:
                status = <AnimatedIconLoading />;
                break;
            default:
        }

        return <span className="VideoSearch__icon">{status}</span>;
    }

    renderStatusMessage() {
        const status = this.state.status;

        return (
            <span key={status} className="VideoSearch__message">
                {messages[status]}
            </span>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/components/VideoSearch/VideoSearch.js